import { useQuery } from '@apollo/client'
import { Close } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { ProductFragment } from 'graphql/generated-types'
import { isEmpty, isNil, uniq } from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import AutoSizer from 'react-virtualized-auto-sizer'
import { useDebounce } from 'use-debounce'

import { BigFilter } from '@/components/big-filter/BigFilter'
import CenteredBox from '@/components/CenteredBox'
import {
  GET_SIMPLE_PRODUCTS,
  GetSimpleProductsQuery,
  GetSimpleProductsQueryVariables,
  MercurySimpleProductFragment,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'

import Shelf from './Shelf'

const dateThreshold = dayjs().subtract(1, 'year').toISOString()

type AddProductControlsProps = {
  onClose: () => void
  setProductToAdd: (
    product: ProductFragment | MercurySimpleProductFragment | null,
  ) => void
  productToAdd: ProductFragment | MercurySimpleProductFragment | null
}

const LIMIT = 20
const VOLUME_REGEX = /(\d+\.?\d*)([a-zA-Z]+)/

const AddProductControls: React.FC<AddProductControlsProps> = ({
  onClose,
  setProductToAdd,
}) => {
  const { t } = useT()
  const { user } = useLoggedInUser()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const [selectedPackagingVolumes, setSelectedPackagingVolumes] = useState<
    string[]
  >([])
  const [selectedPackageQuantities, setSelectedPackageQuantities] = useState<
    string[]
  >(['1']) // Default to single

  const productFilters = useMemo(
    () => ({
      searchTerm: debouncedSearchTerm,
      packagingQuantities: selectedPackageQuantities.map(q => Number(q)),
      orderByRelevancy: true,
      removeZeroRelevancy: true,
      isValidUpc: true,
      lastSeenAfter: dateThreshold,
      packagingSizes: uniq(
        selectedPackagingVolumes
          .map(s => Number(VOLUME_REGEX.exec(s)?.[1]))
          .filter(i => !isNaN(i)),
      ),
      packagingUnits: uniq(
        selectedPackagingVolumes
          .map(s => VOLUME_REGEX.exec(s)?.[2].toUpperCase().trim())
          .filter((s): s is string => !isNil(s))
          .filter(s => !isEmpty(s)),
      ),
    }),
    [debouncedSearchTerm, selectedPackageQuantities, selectedPackagingVolumes],
  )

  const { data, fetchMore, loading, error } = useQuery<
    GetSimpleProductsQuery,
    GetSimpleProductsQueryVariables
  >(GET_SIMPLE_PRODUCTS, {
    variables: {
      filters: productFilters,
      pagination: {
        offset: 0,
        limit: LIMIT,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const handleNext = useCallback(() => {
    if (loading || !data?.products?.results) return
    if (data.products.results.length >= data.products.totalCount) return

    fetchMore({
      variables: {
        filters: productFilters,
        pagination: {
          offset: data.products.results.length,
          limit: LIMIT,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.products?.results) return prev
        return {
          ...prev,
          ...fetchMoreResult,
          products: {
            ...prev.products,
            ...fetchMoreResult.products,
            totalCount: fetchMoreResult.products.totalCount,
            results: [
              ...prev.products.results,
              ...fetchMoreResult.products.results,
            ],
          },
        }
      },
    })
  }, [fetchMore, data, loading, productFilters])

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button sx={{ minWidth: 0, p: 1 }} onClick={() => onClose()}>
          <Close />
        </Button>
      </Box>

      <>
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          <TextField
            fullWidth
            label={t('editPlanogram.addProductControls.search')}
            variant="outlined"
            onChange={e => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
          <BigFilter
            sections={[
              {
                title: t('editPlanogram.addProductControls.packaging'),
                options: [
                  { id: '24oz', label: '24oz' },
                  { id: '20oz', label: '20oz' },
                  { id: '16.9oz', label: '16.9oz' },
                  { id: '16oz', label: '16oz' },
                  { id: '12oz', label: '12oz' },
                  { id: '355ml', label: '355ml' },
                ],
                selectedIds: selectedPackagingVolumes,
                onSelect: id =>
                  setSelectedPackagingVolumes(prev =>
                    prev.includes(id)
                      ? prev.filter(i => i !== id)
                      : [...prev, id],
                  ),
              },
              {
                title: t('editPlanogram.addProductControls.packageQuantity'),
                options: [
                  { id: '1', label: 'Single' },
                  { id: '4', label: '4 pack' },
                  { id: '6', label: '6 pack' },
                  { id: '8', label: '8 pack' },
                  { id: '10', label: '10 pack' },
                  { id: '12', label: '12 pack' },
                  { id: '16', label: '16 pack' },
                  { id: '24', label: '24 pack' },
                ],
                selectedIds: selectedPackageQuantities,
                onSelect: id =>
                  setSelectedPackageQuantities(prev =>
                    prev.includes(id)
                      ? prev.filter(i => i !== id)
                      : [...prev, id],
                  ),
              },
            ]}
          />
        </Box>
        <Box sx={{ height: '100%' }}>
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteScroll
                scrollableTarget="list"
                next={handleNext}
                hasMore={
                  (data?.products.results.length || 0) <
                  (data?.products.totalCount || 0)
                }
                loader={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      p: 2,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                }
                height={height}
                style={{
                  width,
                  border: '1px solid lightGrey',
                  borderRadius: 8,
                }}
                dataLength={data?.products.results.length || 0}
              >
                <Box
                  p={2}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    width: '100%',
                  }}
                >
                  {error ? (
                    <CenteredBox>
                      <Alert severity="error">{error.message}</Alert>
                    </CenteredBox>
                  ) : loading && !data?.products.results.length ? (
                    <CenteredBox>
                      <CircularProgress size={24} />
                    </CenteredBox>
                  ) : (
                    data?.products.results.map(product => {
                      return (
                        <Stack
                          onClick={() => {
                            setProductToAdd(product)
                          }}
                          key={product.id}
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{
                            p: 2,
                            flex: 1,
                            width: '100%',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              overflow: 'hidden',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Shelf
                              facings={[{ id: 'new-product', product }]}
                              listId="product-source"
                              isDropDisabled
                              onFacingMouseDown={() => {
                                setProductToAdd(product)
                              }}
                              onFacingDoubleClick={() => {
                                if (user?.isSuperuser) {
                                  window.open(
                                    `https://admin.deliciousai.com/Products?upc=${product.upcA}&searchTerm=${product.upcA}`,
                                    '_blank',
                                  )
                                }
                              }}
                              alwaysShowDropShadow
                            />
                          </Box>
                          <Stack spacing={0.5} sx={{ flex: 1 }}>
                            <Typography variant="body1">
                              {product.preferredName} {product.packagingName}
                            </Typography>
                            <Typography variant="body1">
                              {product.upcA}
                            </Typography>
                          </Stack>
                        </Stack>
                      )
                    })
                  )}
                </Box>
              </InfiniteScroll>
            )}
          </AutoSizer>
        </Box>
      </>
    </>
  )
}

export default AddProductControls
